import { AlertCircle, CheckCircle2, Loader2, PlusCircle } from "lucide-react";
import { TYPE_NAMES, destinationText } from "@/lib/aeropack";

const lab = "font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500";

export const matchUnit = (units, info) => {
  const d = info?.destination ?? {};
  if (d.kind !== "unit") return null;
  return units.find((u) => (d.id && u.group_id === d.id) || u.name.toLowerCase() === (d.name || "").toLowerCase()) ?? null;
};

const Check = ({ check }) => {
  if (!check) return null;
  if (check.state === "checking") return <p className="flex items-center gap-2 text-xs text-slate-700" data-testid="aeropack-check-running"><Loader2 className="h-3.5 w-3.5 animate-spin" />Checking contents against the package's checksums… {check.done}/{check.total || "?"}</p>;
  const r = check.result;
  if (r?.ok) return <p className="flex items-center gap-2 text-xs font-medium text-emerald-700" data-testid="aeropack-check-ok"><CheckCircle2 className="h-3.5 w-3.5" />All {r.total} data files match the package's checksums.</p>;
  const problems = [...(r?.infoOk === false ? ["info.json"] : []), ...(r?.bad ?? []), ...(r?.missing ?? []).map((m) => `${m} (missing)`)];
  return (
    <p className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="aeropack-check-bad">
      <AlertCircle className="h-4 w-4 shrink-0" />
      <span>{check.error ? `Couldn't check the contents: ${check.error}.` : `These don't match the package's checksums, so the package may be damaged: ${problems.join(", ")}.`} You can still save it.</span>
    </p>
  );
};

export const PackageDetails = ({ info, check }) => (
  <div className="mt-3 space-y-3 rounded-2xl bg-[#F4F9FD] p-4" data-testid="aeropack-details">
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
      {[["Type", `${TYPE_NAMES[info.package_type] ?? info.package_type} · ${info.package_label || ""}`], ["Goes to", destinationText(info)], ["Built with", `${info.app} ${info.app_build ?? ""} ${info.app_stage ?? ""}`], ["Needs build", info.min_build != null ? `${info.min_build} or later` : "Not stated"], ["Made by", info.created_by || "Not stated"], ["Made", info.created_at ? new Date(info.created_at).toLocaleString() : "Not stated"]].map(([k, v]) => (
        <div key={k} className="min-w-0"><p className={lab}>{k}</p><p className="break-words font-medium text-ink" data-testid={`aeropack-${k.toLowerCase().replace(/\s/g, "-")}`}>{v}</p></div>
      ))}
    </div>
    <Check check={check} />
  </div>
);

// Which units get the package. Seed/Config: tick any. Deployment: the unit named in the package. Return: the one unit it came from.
export const UnitPicker = ({ units, info, value, onChange, newUnit }) => {
  const type = info?.package_type;
  const single = type === "return" || type === "deployment";
  const toggle = (id) => onChange(single ? [id] : value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const title = type === "return" ? "Which unit sent this Return?" : type === "deployment" ? "Unit" : "Send to which units?";

  return (
    <div className="mb-3" data-testid="apps-unit-picker">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="mb-1 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{title}</span>
        {!single && units.length > 1 && (
          <button type="button" onClick={() => onChange(value.length === units.length ? [] : units.map((u) => u.id))} className="text-xs font-medium text-sky-700 hover:text-sky-900" data-testid="apps-unit-select-all">
            {value.length === units.length ? "Clear" : "Select all"}
          </button>
        )}
      </div>
      {newUnit && (
        <p className="mb-2 flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900" data-testid="apps-unit-new">
          <PlusCircle className="h-4 w-4 shrink-0" />New unit <b className="mx-1">{newUnit.name}</b> will be added when you save, and its activation code shown on its card.
        </p>
      )}
      {!newUnit && units.length === 0 && <p className="text-xs text-slate-600" data-testid="apps-unit-none">Add a unit first.</p>}
      {!newUnit && (
        <div className="flex flex-wrap gap-2">
          {units.map((u) => {
            const on = value.includes(u.id);
            return (
              <button key={u.id} type="button" onClick={() => toggle(u.id)} aria-pressed={on}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "border-sky-600 bg-sky-600 text-white" : "border-slate-200 bg-white text-ink hover:border-sky-600"}`} data-testid={`apps-unit-option-${u.id}`}>
                {u.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
